// src/pages/pricing.js

import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FeedbackRoundedIcon from "@mui/icons-material/FeedbackRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";

import LandingLayout from "@/components/layout/LandingLayout";

const connectionPlans = [
  {
    key: "online",
    eyebrow: "Работа через браузер",
    title: "СпортПодсчет Хоккей Online",
    subtitle:
      "Для площадок, которым важен доступ с разных компьютеров и мобильных устройств.",
    price: "Индивидуально",
    priceCaption: "после демонстрации",
    accent: "#00A7D8",
    icon: CloudOutlinedIcon,
    features: [
      "Проведение полноценных хоккейных матчей",
      "Хранение матчей и команд на сервере",
      "Управление временем, счётом и удалениями",
      "Зрительское табло на отдельном экране",
      "Управление с компьютера, телефона или планшета",
      "Помощь с первоначальной настройкой",
      "Техническая поддержка в период тестирования",
    ],
    button: "Подать заявку на Online",
    action: "online",
  },
  {
    key: "pilot",
    eyebrow: "Специальное предложение",
    title: "Пилотная площадка",
    subtitle:
      "Для тех, кто готов проводить реальные или учебные матчи и делиться обратной связью.",
    price: "Специальные условия",
    priceCaption: "на период пилота",
    accent: "#5865E8",
    icon: ScienceRoundedIcon,
    featured: true,
    badge: "Приоритетное подключение",
    features: [
      "Расширенный период тестирования системы",
      "Индивидуальные условия участия",
      "Помощь с подготовкой рабочего места",
      "Настройка панели оператора и зрительского табло",
      "Обучение оператора перед первыми матчами",
      "Приоритетная техническая поддержка",
      "Прямой контакт с разработчиком",
      "Возможность влиять на развитие новых функций",
    ],
    button: "Стать участником пилота",
    action: "pilot",
  },
  {
    key: "local",
    eyebrow: "Автономная работа",
    title: "СпортПодсчет Хоккей Local",
    subtitle:
      "Для постоянного компьютера оператора и работы без зависимости от интернета.",
    price: "По запросу",
    priceCaption: "индивидуальная конфигурация",
    accent: "#0073FF",
    icon: ComputerRoundedIcon,
    features: [
      "Установка на компьютер хоккейной площадки",
      "Работа без постоянного подключения к интернету",
      "Локальное хранение матчей и команд",
      "Управление временем, счётом и удалениями",
      "Вывод табло на монитор, телевизор или LED-экран",
      "Первоначальная настройка программы",
      "Обучение оператора",
      "Сопровождение первых матчей",
    ],
    button: "Обсудить локальную версию",
    action: "local",
  },
];

const pilotBenefits = [
  {
    icon: HandshakeRoundedIcon,
    title: "Индивидуальные условия",
    description:
      "Формат участия определяется с учётом количества матчей, оборудования, состава операторов и задач площадки.",
  },
  {
    icon: SettingsSuggestRoundedIcon,
    title: "Помощь с настройкой",
    description:
      "Поможем подготовить компьютер оператора, настроить параметры матча и вывести табло на отдельный экран.",
  },
  {
    icon: SupportAgentRoundedIcon,
    title: "Поддержка первых матчей",
    description:
      "Разберём вопросы оператора и поможем устранить сложности, выявленные при реальном использовании системы.",
  },
  {
    icon: FeedbackRoundedIcon,
    title: "Влияние на развитие",
    description:
      "Предложения пилотных площадок учитываются при доработке интерфейса и определении приоритетных функций.",
  },
];

const pilotSteps = [
  {
    number: "01",
    title: "Знакомимся с площадкой",
    description:
      "Обсуждаем формат матчей, оборудование, рабочее место оператора и способ вывода зрительского табло.",
  },
  {
    number: "02",
    title: "Настраиваем систему",
    description:
      "Помогаем выбрать локальную или онлайн-версию, провести настройку и подготовить оператора.",
  },
  {
    number: "03",
    title: "Проводим матчи",
    description:
      "Вы используете СпортПодсчет Хоккей на учебных или реальных матчах и сообщаете о замеченных сложностях.",
  },
  {
    number: "04",
    title: "Улучшаем продукт",
    description:
      "Разбираем обратную связь, исправляем проблемы и согласовываем дальнейший формат использования.",
  },
];

const faqItems = [
  {
    question: "Сколько стоит участие в пилотной программе?",
    answer:
      "Условия определяются индивидуально после демонстрации и обсуждения сценария работы. Учитываются выбранная версия, количество матчей, оборудование площадки и необходимый объём сопровождения.",
  },
  {
    question: "Кто может стать пилотной площадкой?",
    answer:
      "К участию приглашаются ледовые арены, спортивные школы, хоккейные клубы, любительские лиги и организаторы турниров, готовые использовать систему на учебных или реальных матчах.",
  },
  {
    question: "Нужно ли составлять подробные отчёты?",
    answer:
      "Нет. Достаточно сообщать о найденных ошибках, неудобных действиях и пожеланиях оператора. Обратную связь можно передавать в удобной форме после проведённых матчей.",
  },
  {
    question: "Можно ли тестировать систему на официальных матчах?",
    answer:
      "Это зависит от требований соревнований, готовности оборудования и выбранной версии. Сначала рекомендуется провести несколько учебных или товарищеских матчей и проверить работу системы в условиях конкретной площадки.",
  },
  {
    question: "Подача заявки обязывает приобрести систему?",
    answer:
      "Нет. Подача заявки нужна для знакомства с площадкой и обсуждения условий. Порядок тестирования и дальнейшего использования согласовывается отдельно до начала пилота.",
  },
  {
    question: "Можно ли участвовать только в тестировании онлайн-версии?",
    answer:
      "Да. Пилот может проводиться для онлайн-версии, локальной версии или для конкретного сценария работы, например управления матчем с телефона или вывода табло на большой экран.",
  },
];

const breadcrumbStructuredData = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Главная",
      item: "https://sportpodschet.partner-progress.ru/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Условия подключения",
      item: "https://sportpodschet.partner-progress.ru/pricing",
    },
  ],
};

function PlanCard({
  plan,
  isDark,
  onAction,
}) {
  const Icon = plan.icon;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",

        borderRadius: {
          xs: 4,
          md: 5,
        },

        border: `1px solid ${alpha(
          plan.accent,
          plan.featured
            ? isDark
              ? 0.56
              : 0.42
            : isDark
              ? 0.3
              : 0.2
        )}`,

        bgcolor: plan.featured
          ? isDark
            ? alpha(plan.accent, 0.12)
            : alpha(plan.accent, 0.045)
          : "background.paper",

        boxShadow: plan.featured
          ? isDark
            ? "0 30px 80px rgba(0,0,0,0.35)"
            : "0 32px 85px rgba(60,75,180,0.18)"
          : isDark
            ? "0 20px 55px rgba(0,0,0,0.18)"
            : "0 20px 55px rgba(35,70,120,0.08)",

        transform: {
          xs: "none",
          md: plan.featured
            ? "translateY(-10px)"
            : "none",
        },

        transition:
          "transform .25s ease, box-shadow .25s ease",

        "&:hover": {
          transform: {
            xs: "translateY(-5px)",
            md: plan.featured
              ? "translateY(-16px)"
              : "translateY(-6px)",
          },

          boxShadow: plan.featured
            ? isDark
              ? "0 38px 90px rgba(0,0,0,0.42)"
              : "0 40px 95px rgba(60,75,180,0.24)"
            : isDark
              ? "0 28px 70px rgba(0,0,0,0.28)"
              : "0 28px 70px rgba(35,70,120,0.13)",
        },
      }}
    >
      {/* Верхняя цветная полоса */}

      <Box
        sx={{
          height: plan.featured ? 7 : 5,
          flex: "0 0 auto",
          background: plan.featured
            ? "linear-gradient(90deg, #005BDA 0%, #5865E8 52%, #00A7D8 100%)"
            : plan.accent,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 250,
          height: 250,
          top: -145,
          right: -110,
          borderRadius: "50%",
          bgcolor: alpha(
            plan.accent,
            isDark ? 0.15 : 0.07
          ),
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",

          p: {
            xs: 3,
            md: 3.5,
            lg: 4,
          },
        }}
      >
        {/* Верх карточки */}

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              borderRadius: 2.8,
              color: "#FFFFFF",
              bgcolor: plan.accent,
              boxShadow: `0 12px 28px ${alpha(
                plan.accent,
                0.28
              )}`,
            }}
          >
            <Icon
              sx={{
                fontSize: 29,
              }}
            />
          </Box>

          {plan.badge && (
            <Chip
              size="small"
              label={plan.badge}
              sx={{
                maxWidth: 190,
                height: "auto",
                py: 0.5,
                color: plan.accent,
                bgcolor: alpha(
                  plan.accent,
                  isDark ? 0.17 : 0.09
                ),
                border: `1px solid ${alpha(
                  plan.accent,
                  0.25
                )}`,
                fontWeight: 800,

                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "normal",
                  textAlign: "center",
                },
              }}
            />
          )}
        </Stack>

        <Typography
          component="p"
          sx={{
            mt: 2.7,
            color: plan.accent,
            fontSize: 12.5,
            lineHeight: 1.4,
            fontWeight: 850,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {plan.eyebrow}
        </Typography>

        <Typography
          component="h2"
          sx={{
            mt: 0.8,
            fontSize: {
              xs: "1.8rem",
              md: "2rem",
            },
            lineHeight: 1.12,
            fontWeight: 850,
            letterSpacing: "-0.035em",
          }}
        >
          {plan.title}
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            minHeight: {
              md: 78,
            },
            mt: 1.3,
            lineHeight: 1.65,
          }}
        >
          {plan.subtitle}
        </Typography>

        {/* Условия */}

        <Box
          sx={{
            mt: 2.5,
            px: 2.2,
            py: 2,
            borderRadius: 3,
            bgcolor: alpha(
              plan.accent,
              isDark ? 0.11 : 0.055
            ),
            border: `1px solid ${alpha(
              plan.accent,
              isDark ? 0.26 : 0.16
            )}`,
          }}
        >
          <Typography
            sx={{
              color: plan.accent,
              fontSize: {
                xs: "1.65rem",
                md: plan.featured
                  ? "1.85rem"
                  : "1.75rem",
              },
              lineHeight: 1.15,
              fontWeight: 900,
              letterSpacing: "-0.035em",
            }}
          >
            {plan.price}
          </Typography>

          <Typography
            color="text.secondary"
            sx={{
              mt: 0.5,
              fontSize: 13.5,
              fontWeight: 650,
            }}
          >
            {plan.priceCaption}
          </Typography>
        </Box>

        {/* Функции */}

        <Stack
          spacing={1.35}
          sx={{
            mt: 3,
          }}
        >
          {plan.features.map((feature) => (
            <Stack
              key={feature}
              direction="row"
              spacing={1}
              alignItems="flex-start"
            >
              <CheckCircleRoundedIcon
                sx={{
                  mt: 0.2,
                  flex: "0 0 auto",
                  color: plan.accent,
                  fontSize: 20,
                }}
              />

              <Typography
                sx={{
                  fontSize: 14.5,
                  lineHeight: 1.55,
                  fontWeight: 650,
                }}
              >
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {/* Кнопка */}

        <Box
          sx={{
            mt: "auto",
            pt: 3.5,
          }}
        >
          <Button
            fullWidth
            variant={
              plan.featured
                ? "contained"
                : "outlined"
            }
            size="large"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => onAction(plan.action)}
            sx={{
              minHeight: 54,
              px: 2.5,
              borderRadius: 2.5,
              borderColor: plan.accent,
              bgcolor: plan.featured
                ? plan.accent
                : "transparent",
              color: plan.featured
                ? "#FFFFFF"
                : plan.accent,
              textTransform: "none",
              fontSize: 15,
              fontWeight: 850,

              "&:hover": {
                borderColor: plan.accent,
                bgcolor: plan.featured
                  ? plan.accent
                  : alpha(plan.accent, 0.07),
                filter: plan.featured
                  ? "brightness(0.94)"
                  : "none",
              },
            }}
          >
            {plan.button}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default function PricingPage() {
  const router = useRouter();
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const openDemo = () => {
    router.push("/demo");
  };

  const openContacts = () => {
    router.push("/contacts");
  };

  const handlePlanAction = (action) => {
    router.push({
      pathname: "/contacts",
      query: {
        interest: action,
      },
    });
  };

  return (
    <>
      <Head>
        <title>
          Пилотная программа СпортПодсчет Хоккей — условия подключения
        </title>

        <meta
          name="description"
          content="Условия подключения СпортПодсчет Хоккей. Индивидуальные условия для участников пилотной программы, онлайн-версия и автономное локальное решение для хоккейных площадок."
        />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <link
          rel="canonical"
          href="https://sportpodschet.partner-progress.ru/pricing"
        />

        <meta
          property="og:title"
          content="Пилотная программа СпортПодсчет Хоккей"
        />

        <meta
          property="og:description"
          content="Специальные условия для хоккейных площадок, готовых тестировать СпортПодсчет Хоккей на учебных и реальных матчах."
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:locale"
          content="ru_RU"
        />

        <meta
          property="og:url"
          content="https://sportpodschet.partner-progress.ru/pricing"
        />

        <meta
          property="og:image"
          content="https://sportpodschet.partner-progress.ru/screens/control_panel.webp"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              breadcrumbStructuredData
            ),
          }}
        />
      </Head>

      <Box
        component="main"
        sx={{
          overflow: "hidden",
        }}
      >
        {/* Первый экран */}

        <Box
          component="header"
          sx={{
            position: "relative",
            overflow: "hidden",

            pt: {
              xs: 8,
              md: 11,
            },

            pb: {
              xs: 8,
              md: 11,
            },

            background: isDark
              ? `
                  linear-gradient(
                    135deg,
                    rgba(8,19,36,0.98) 0%,
                    rgba(8,35,65,0.94) 48%,
                    rgba(28,40,92,0.90) 100%
                  )
                `
              : `
                  linear-gradient(
                    135deg,
                    rgba(248,251,255,0.98) 0%,
                    rgba(232,243,255,0.95) 48%,
                    rgba(234,235,255,0.92) 100%
                  )
                `,

            borderBottom: `1px solid ${alpha(
              theme.palette.text.primary,
              0.06
            )}`,

            "&::before": {
              content: '""',
              position: "absolute",

              width: {
                xs: 430,
                md: 760,
              },

              height: {
                xs: 430,
                md: 760,
              },

              top: {
                xs: -250,
                md: -420,
              },

              right: {
                xs: -250,
                md: -190,
              },

              borderRadius: "50%",

              background:
                "radial-gradient(circle, rgba(88,101,232,0.24) 0%, rgba(0,167,216,0.08) 48%, transparent 72%)",

              pointerEvents: "none",
            },

            "&::after": {
              content: '""',
              position: "absolute",

              width: 560,
              height: 560,

              left: -330,
              bottom: -390,

              borderRadius: "50%",

              background:
                "radial-gradient(circle, rgba(0,115,255,0.15) 0%, transparent 70%)",

              pointerEvents: "none",
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: 1100,
              textAlign: "center",
            }}
          >
            <Typography
              component="p"
              sx={{
                color: "primary.main",
                fontSize: 13,
                lineHeight: 1.4,
                fontWeight: 850,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Пилотная программа
            </Typography>

            <Typography
              component="h1"
              sx={{
                maxWidth: 980,
                mx: "auto",
                mt: 1.8,

                fontSize: {
                  xs: "2.4rem",
                  sm: "3.15rem",
                  md: "4rem",
                },

                lineHeight: 1.05,
                fontWeight: 850,
                letterSpacing: "-0.05em",
              }}
            >
              Индивидуальные условия для первых площадок
              СпортПодсчет Хоккей
            </Typography>

            <Typography
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 900,
                mx: "auto",
                mt: 2.5,

                fontSize: {
                  xs: 16.5,
                  md: 19,
                },

                lineHeight: 1.7,
              }}
            >
              Подключаем хоккейные площадки, школы, клубы и
              организаторов турниров к реальному тестированию
              системы. Участники пилота получают помощь с
              запуском, техническую поддержку и возможность
              влиять на развитие продукта.
            </Typography>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1.5}
              justifyContent="center"
              sx={{
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<RocketLaunchRoundedIcon />}
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={openContacts}
                sx={{
                  minHeight: 54,
                  px: 3.4,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 850,
                  boxShadow:
                    "0 12px 30px rgba(0,115,255,0.24)",
                }}
              >
                Подать заявку на пилот
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={
                  <PlayCircleOutlineRoundedIcon />
                }
                onClick={openDemo}
                sx={{
                  minHeight: 54,
                  px: 3.4,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 850,

                  bgcolor: isDark
                    ? "rgba(255,255,255,0.035)"
                    : "rgba(255,255,255,0.55)",

                  backdropFilter: "blur(12px)",
                }}
              >
                Посмотреть демонстрацию
              </Button>
            </Stack>

            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              gap={1}
              sx={{
                mt: 3,
              }}
            >
              {[
                "Индивидуальные условия",
                "Настройка системы",
                "Обучение оператора",
                "Поддержка первых матчей",
              ].map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.055)"
                      : "rgba(255,255,255,0.62)",

                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      isDark ? 0.2 : 0.12
                    )}`,

                    fontWeight: 700,
                    backdropFilter: "blur(10px)",
                  }}
                />
              ))}
            </Stack>

            <Typography
              color="text.secondary"
              sx={{
                maxWidth: 720,
                mx: "auto",
                mt: 3,
                fontSize: 14,
                lineHeight: 1.65,
              }}
            >
              Подключение проводится поэтапно, чтобы мы могли
              уделить внимание настройке и сопровождению каждой
              пилотной площадки.
            </Typography>
          </Container>
        </Box>

        {/* Варианты подключения */}

        <Box
          component="section"
          id="connection-options"
          sx={{
            py: {
              xs: 8,
              md: 12,
            },
            bgcolor: "background.default",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Box
              sx={{
                maxWidth: 850,
                mx: "auto",
                mb: {
                  xs: 5,
                  md: 7,
                },
                textAlign: "center",
              }}
            >
              <Typography
                component="p"
                sx={{
                  color: "primary.main",
                  fontSize: 13,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                Условия подключения
              </Typography>

              <Typography
                component="h2"
                sx={{
                  mt: 1.5,

                  fontSize: {
                    xs: "2rem",
                    md: "3rem",
                  },

                  lineHeight: 1.1,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                Выберите подходящий формат участия
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 760,
                  mx: "auto",
                  mt: 2,

                  fontSize: {
                    xs: 16,
                    md: 17,
                  },

                  lineHeight: 1.7,
                }}
              >
                Итоговые условия зависят от выбранной версии,
                количества матчей, состава оборудования и
                необходимого объёма сопровождения.
              </Typography>
            </Box>

            <Grid
              container
              spacing={{
                xs: 3,
                md: 3,
              }}
              alignItems="stretch"
              justifyContent="center"
            >
              {connectionPlans.map((plan) => (
                <Grid
                  key={plan.key}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <PlanCard
                    plan={plan}
                    isDark={isDark}
                    onAction={handlePlanAction}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Преимущества пилота */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 11,
            },

            bgcolor: isDark
              ? "rgba(255,255,255,0.025)"
              : "#F6F8FC",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1320,
            }}
          >
            <Box
              sx={{
                maxWidth: 850,
                mx: "auto",
                mb: {
                  xs: 5,
                  md: 7,
                },
                textAlign: "center",
              }}
            >
              <Typography
                component="p"
                sx={{
                  color: "#5865E8",
                  fontSize: 13,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                Участникам пилота
              </Typography>

              <Typography
                component="h2"
                sx={{
                  mt: 1.5,

                  fontSize: {
                    xs: "2rem",
                    md: "3rem",
                  },

                  lineHeight: 1.1,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                Не просто доступ к программе, а помощь с
                реальным запуском
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 780,
                  mx: "auto",
                  mt: 2,

                  fontSize: {
                    xs: 16,
                    md: 17,
                  },

                  lineHeight: 1.7,
                }}
              >
                Пилотная программа рассчитана на практическую
                работу. Мы помогаем подготовить систему, а
                участник проверяет её во время настоящих или
                учебных хоккейных матчей.
              </Typography>
            </Box>

            <Grid
              container
              spacing={2.5}
            >
              {pilotBenefits.map((item) => {
                const Icon = item.icon;

                return (
                  <Grid
                    key={item.title}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 3,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        height: "100%",

                        p: {
                          xs: 3,
                          md: 3.2,
                        },

                        borderRadius: 4,

                        border: `1px solid ${alpha(
                          "#5865E8",
                          isDark ? 0.24 : 0.13
                        )}`,

                        bgcolor: "background.paper",

                        transition:
                          "transform .25s ease, box-shadow .25s ease",

                        "&:hover": {
                          transform: "translateY(-5px)",

                          boxShadow: isDark
                            ? "0 24px 60px rgba(0,0,0,0.24)"
                            : "0 24px 60px rgba(50,70,140,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,

                          display: "grid",
                          placeItems: "center",

                          borderRadius: 2.8,

                          color: "#5865E8",

                          bgcolor: alpha(
                            "#5865E8",
                            isDark ? 0.16 : 0.08
                          ),
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 28,
                          }}
                        />
                      </Box>

                      <Typography
                        component="h3"
                        sx={{
                          mt: 2.5,
                          fontSize: 19,
                          lineHeight: 1.3,
                          fontWeight: 850,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          fontSize: 15,
                          lineHeight: 1.7,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* Как проходит пилот */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
            bgcolor: "background.default",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1250,
            }}
          >
            <Grid
              container
              spacing={{
                xs: 5,
                md: 8,
              }}
              alignItems="flex-start"
            >
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Box
                  sx={{
                    position: {
                      md: "sticky",
                    },
                    top: {
                      md: 110,
                    },
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      color: "primary.main",
                      fontSize: 13,
                      fontWeight: 850,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                    }}
                  >
                    Порядок подключения
                  </Typography>

                  <Typography
                    component="h2"
                    sx={{
                      mt: 1.5,

                      fontSize: {
                        xs: "2rem",
                        md: "2.8rem",
                      },

                      lineHeight: 1.12,
                      fontWeight: 850,
                      letterSpacing: "-0.04em",
                    }}
                  >
                    Как проходит участие в пилоте
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      mt: 2,
                      fontSize: 16,
                      lineHeight: 1.75,
                    }}
                  >
                    От площадки не требуется составлять сложные
                    технические отчёты. Главное — использовать
                    систему и сообщать о реальном опыте
                    оператора.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<TaskAltRoundedIcon />}
                    endIcon={<ArrowForwardRoundedIcon />}
                    onClick={openContacts}
                    sx={{
                      mt: 3.5,
                      minHeight: 52,
                      px: 3.2,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 850,
                    }}
                  >
                    Подать заявку
                  </Button>
                </Box>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 8,
                }}
              >
                <Stack spacing={2}>
                  {pilotSteps.map((item) => (
                    <Paper
                      key={item.number}
                      elevation={0}
                      sx={{
                        position: "relative",
                        overflow: "hidden",

                        display: "flex",
                        alignItems: "flex-start",
                        gap: {
                          xs: 2,
                          sm: 2.5,
                        },

                        p: {
                          xs: 2.5,
                          sm: 3,
                        },

                        borderRadius: 4,

                        border: `1px solid ${alpha(
                          theme.palette.text.primary,
                          isDark ? 0.13 : 0.08
                        )}`,

                        bgcolor: isDark
                          ? "rgba(255,255,255,0.03)"
                          : "#FFFFFF",
                      }}
                    >
                      <Box
                        sx={{
                          width: 54,
                          height: 54,
                          flex: "0 0 auto",

                          display: "grid",
                          placeItems: "center",

                          borderRadius: 2.8,

                          color: "#FFFFFF",

                          background:
                            "linear-gradient(135deg, #005BDA 0%, #5865E8 100%)",

                          fontSize: 15,
                          fontWeight: 900,
                        }}
                      >
                        {item.number}
                      </Box>

                      <Box>
                        <Typography
                          component="h3"
                          sx={{
                            fontSize: {
                              xs: 18,
                              md: 20,
                            },
                            lineHeight: 1.3,
                            fontWeight: 850,
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography
                          color="text.secondary"
                          sx={{
                            mt: 0.7,
                            fontSize: 15,
                            lineHeight: 1.7,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Для кого */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 7,
              md: 9,
            },

            bgcolor: isDark
              ? alpha("#0073FF", 0.06)
              : alpha("#0073FF", 0.035),
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1200,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 3,
                  md: 4,
                },

                borderRadius: 5,

                border: `1px solid ${alpha(
                  "#0073FF",
                  isDark ? 0.24 : 0.14
                )}`,

                bgcolor: isDark
                  ? "rgba(255,255,255,0.025)"
                  : "rgba(255,255,255,0.72)",
              }}
            >
              <Grid
                container
                spacing={{
                  xs: 3,
                  md: 5,
                }}
                alignItems="center"
              >
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.3}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        flex: "0 0 auto",

                        display: "grid",
                        placeItems: "center",

                        borderRadius: 2.8,

                        color: "#FFFFFF",
                        bgcolor: "#0073FF",
                      }}
                    >
                      <GroupsRoundedIcon />
                    </Box>

                    <Box>
                      <Typography
                        component="h2"
                        sx={{
                          fontSize: 21,
                          lineHeight: 1.25,
                          fontWeight: 850,
                        }}
                      >
                        Кого приглашаем
                      </Typography>

                      <Typography
                        color="text.secondary"
                        sx={{
                          mt: 0.4,
                          fontSize: 14,
                        }}
                      >
                        Площадки разного уровня
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                >
                  <Stack
                    direction="row"
                    useFlexGap
                    flexWrap="wrap"
                    gap={1}
                  >
                    {[
                      "Ледовые арены",
                      "Спортивные школы",
                      "Хоккейные клубы",
                      "Любительские лиги",
                      "Организаторы турниров",
                      "Судьи-секретари",
                      "Поставщики оборудования",
                    ].map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        sx={{
                          height: 38,

                          bgcolor: isDark
                            ? "rgba(255,255,255,0.05)"
                            : "#FFFFFF",

                          border: `1px solid ${alpha(
                            "#0073FF",
                            0.13
                          )}`,

                          fontWeight: 750,
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Container>
        </Box>

        {/* Частые вопросы */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
            bgcolor: "background.default",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 960,
            }}
          >
            <Box
              sx={{
                maxWidth: 760,
                mx: "auto",
                mb: {
                  xs: 4,
                  md: 5,
                },
                textAlign: "center",
              }}
            >
              <Typography
                component="p"
                sx={{
                  color: "primary.main",
                  fontSize: 13,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                Частые вопросы
              </Typography>

              <Typography
                component="h2"
                sx={{
                  mt: 1.5,

                  fontSize: {
                    xs: "2rem",
                    md: "2.8rem",
                  },

                  lineHeight: 1.12,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                Об участии и условиях тестирования
              </Typography>
            </Box>

            <Stack spacing={1.3}>
              {faqItems.map((item) => (
                <Accordion
                  key={item.question}
                  disableGutters
                  elevation={0}
                  sx={{
                    overflow: "hidden",

                    borderRadius: "16px !important",

                    border: `1px solid ${alpha(
                      theme.palette.text.primary,
                      isDark ? 0.13 : 0.08
                    )}`,

                    bgcolor: isDark
                      ? "rgba(255,255,255,0.025)"
                      : "#FFFFFF",

                    "&::before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreRoundedIcon />
                    }
                    sx={{
                      px: {
                        xs: 2.2,
                        md: 3,
                      },

                      py: 0.5,

                      "& .MuiAccordionSummary-content": {
                        my: 1.5,
                      },
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        fontSize: {
                          xs: 16,
                          md: 17,
                        },
                        lineHeight: 1.45,
                        fontWeight: 800,
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      px: {
                        xs: 2.2,
                        md: 3,
                      },
                      pt: 0,
                      pb: 2.5,
                    }}
                  >
                    <Typography
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.75,
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Финальный призыв */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 11,
            },

            bgcolor: isDark
              ? "rgba(255,255,255,0.025)"
              : "#F6F8FC",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",

                px: {
                  xs: 3,
                  sm: 5,
                  md: 8,
                },

                py: {
                  xs: 5,
                  md: 7,
                },

                borderRadius: {
                  xs: 4,
                  md: 6,
                },

                color: "#FFFFFF",

                background:
                  "linear-gradient(135deg, #005BDA 0%, #5865E8 52%, #00A7D8 100%)",

                boxShadow:
                  "0 30px 80px rgba(50,80,200,0.25)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",

                  width: 400,
                  height: 400,

                  right: -120,
                  top: -190,

                  borderRadius: "50%",

                  bgcolor: "rgba(255,255,255,0.10)",

                  pointerEvents: "none",
                }}
              />

              <Grid
                container
                spacing={{
                  xs: 4,
                  md: 6,
                }}
                alignItems="center"
                sx={{
                  position: "relative",
                }}
              >
                <Grid
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      mb: 1.4,
                      fontSize: 13,
                      fontWeight: 850,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    Первые площадки
                  </Typography>

                  <Typography
                    component="h2"
                    sx={{
                      maxWidth: 850,

                      fontSize: {
                        xs: "2.1rem",
                        md: "3.2rem",
                      },

                      lineHeight: 1.08,
                      fontWeight: 850,
                      letterSpacing: "-0.04em",
                    }}
                  >
                    Проведите первые матчи вместе с СпортПодсчет
                    Хоккей
                  </Typography>

                  <Typography
                    sx={{
                      maxWidth: 820,
                      mt: 2,

                      fontSize: {
                        xs: 16,
                        md: 18,
                      },

                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.86)",
                    }}
                  >
                    Расскажите о своей площадке и формате матчей.
                    Мы покажем систему, предложим подходящий
                    сценарий тестирования и обсудим
                    индивидуальные условия участия.
                  </Typography>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack
                    spacing={1.5}
                    alignItems={{
                      xs: "stretch",
                      md: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<EmojiEventsRoundedIcon />}
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={openContacts}
                      sx={{
                        minHeight: 54,
                        px: 3.4,
                        borderRadius: 2.5,
                        bgcolor: "#FFFFFF",
                        color: "#3546C8",
                        textTransform: "none",
                        fontSize: 16,
                        fontWeight: 850,

                        "&:hover": {
                          bgcolor:
                            "rgba(255,255,255,0.92)",
                        },
                      }}
                    >
                      Подать заявку на пилот
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={
                        <PlayCircleOutlineRoundedIcon />
                      }
                      onClick={openDemo}
                      sx={{
                        minHeight: 52,
                        px: 3.3,
                        borderRadius: 2.5,

                        borderColor:
                          "rgba(255,255,255,0.58)",

                        color: "#FFFFFF",

                        textTransform: "none",
                        fontWeight: 800,

                        "&:hover": {
                          borderColor: "#FFFFFF",
                          bgcolor:
                            "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      Посмотреть демонстрацию
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
}

PricingPage.getLayout = (page) => (
  <LandingLayout>{page}</LandingLayout>
);